import { jwtDecode } from "jwt-decode";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as api from "../../api/index";
import { IoMdArrowBack, IoMdRefresh } from "react-icons/io";
import { FaMapMarkerAlt, FaBoxOpen } from "react-icons/fa";
import productData from "../Outerpage/productlist";

// Lagos LGAs
const lagosLGAs = [
  "All Lagos", "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa", "Badagry",
  "Epe", "Eti-Osa", "Ibeju-Lekki", "Ifako-Ijaiye", "Ikeja", "Ikorodu", "Kosofe",
  "Lagos Island", "Lagos Mainland", "Mushin", "Ojo", "Oshodi-Isolo",
  "Shomolu", "Surulere"
];

export default function ConnectProduct() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialProduct = location.state?.product || "";
  const initialSubCategory = location.state?.subCategory || "";
  const initialModel = location.state?.model || "";
  const category = location.state?.category || "";

  const token = localStorage.getItem("token");
  let userId = null;
  let typeofuser = null;

  if (token) {
    try {
      const decoded = jwtDecode(token);
      userId = decoded?._id || decoded?.id;
      typeofuser = decoded?.typeofuser;
    } catch {}
  }

  // ✅ STATE (All your logic preserved)
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lga, setLga] = useState("All Lagos");
  const [error, setError] = useState("");
  const [relatedBrands, setRelatedBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(initialProduct);
  const [selectedSubCategory, setSelectedSubCategory] = useState(initialSubCategory);
  const [selectedModel, setSelectedModel] = useState(initialModel);
  const [sortFilter, setSortFilter] = useState("All");
 
const [productSearch, setProductSearch] = useState("");
const [searchingProduct, setSearchingProduct] = useState(false);



// Inside ConnectProduct.jsx

const fetchProducts = async (forceRefresh = false) => {
  const aiPrompt = location.state?.aiPrompt; // Check if we came from AI search
  const navKey = location.key || "default";
  const cacheKey = `prod_cache_${navKey}_${lga}_${selectedProduct}_${aiPrompt || 'standard'}`;
  
  const cachedData = sessionStorage.getItem(cacheKey);
  if (cachedData && !forceRefresh) {
    setProducts(JSON.parse(cachedData));
    setLoading(false);
    return;
  }

  try {
    setLoading(true);
    let res;

    if (aiPrompt) {
      // 🚀 USE CLAUDE AI SEARCH
      res = await api.fetchAIProductSearch({ 
        prompt: aiPrompt, 
        userId: userId 
      });
    } else {
      // 🛠️ USE STANDARD FILTER SEARCH
      const queryParams = { 
        lga, 
        userId: userId || "", 
        product: selectedProduct,
        model: selectedModel,
        subCategory: selectedSubCategory,
        newlyAdded: sortFilter === "Newly Added"
      };
      res = await api.fetchProductData(queryParams);
    }

    if (res.data.success) {
      const productList = res.data.products || [];
      setProducts(productList);
      sessionStorage.setItem(cacheKey, JSON.stringify(productList));
      setError("");
    } else {
      setProducts([]);
      setError("No products found.");
    }
  } catch (err) {
    setError("Failed to load products.");
  } finally {
    setLoading(false);
  }
};

const handleProductSearch = async () => {
  const value = productSearch.trim();

  if (!value) return;

  try {
    setSearchingProduct(true);
    setError("");

    const res = await api.searchProductByName(value, userId);

    if (res.data?.success && res.data?.product?._id) {
      navigate(`/view-product/${res.data.product._id}`);
    } else {
      setError("No product found.");
    }
  } catch (err) {
    console.error("PRODUCT SEARCH ERROR:", err);

    setError(
      err.response?.data?.message || "Product not found."
    );
  } finally {
    setSearchingProduct(false);
  }
};


  const refreshPage = () => fetchProducts(true);

  // ✅ Brand/Model matching logic (Your Logic)
  useEffect(() => {
    if (!selectedProduct && !selectedSubCategory && !category) {
      setRelatedBrands([]);
      setModels([]);
      return;
    }
    const selected = selectedProduct?.toLowerCase() || "";
    const subCat = selectedSubCategory?.toLowerCase() || "";

    const matchedBlock = productData.find(
      (block) =>
        block.subcategory?.toLowerCase() === subCat ||
        block.products.some(
          (p) => p.name.toLowerCase() === selected || p.models.some((m) => m.toLowerCase() === selected)
        )
    );

    if (matchedBlock) {
      setRelatedBrands(matchedBlock.products.map((p) => p.name));
      const selectedProd = matchedBlock.products.find((p) => p.name.toLowerCase() === selected);
      setModels(selectedProd ? selectedProd.models : []);
    } else {
      setRelatedBrands([]);
      setModels([]);
    }
  }, [selectedProduct, selectedSubCategory, category]);

  useEffect(() => {
    fetchProducts();
  }, [lga, selectedProduct, selectedModel, selectedSubCategory, sortFilter]);

  // ✅ Redirect logic (Your Logic)
  useEffect(() => {
    if (!loading && products.length === 0) {
      const timeout = setTimeout(() => {
        navigate("/connect", {
          state: {
            aiPrompt: location.state?.aiPrompt,
            itemsShow: selectedProduct || "",
            subCategory: selectedSubCategory || "",
            category: category || "",
          },
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [loading, products]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-20">
      {/* 🔝 Navbar (Premium Pattern) */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex justify-between items-center shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition flex items-center gap-1 text-gray-700"
        >
          <IoMdArrowBack size={24} />
          <span className="hidden sm:inline font-medium">Back</span>
        </button>

        <button
          onClick={refreshPage}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-xl hover:bg-teal-700 transition text-sm font-semibold"
        >
          <IoMdRefresh size={18} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="max-w-6xl mx-auto w-full p-4 sm:p-6 space-y-6">
       
{/* Product Link/ID Search */}

<div className="px-4 pt-3">
  <div className="max-w-6xl mx-auto">
    <div className="flex gap-2 mt-2">
      <input
        type="text"
        value={productSearch}
        onChange={(e) => setProductSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleProductSearch();
        }}
        placeholder="Paste product ID or product link"
        className="flex-1 bg-white border border-gray-200 px-4 py-3 rounded-2xl outline-none focus:ring-2 focus:ring-orange-500 text-sm"
      />

      <button
        onClick={handleProductSearch}
        disabled={searchingProduct || !productSearch.trim()}
        className="bg-orange-600 text-white px-5 rounded-2xl font-bold text-sm disabled:opacity-50"
      >
        {searchingProduct ? "..." : "Search"}
      </button>
    </div>
  </div>
</div>


        {/* 🏷 Header & Filter Section (Premium Pattern) */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-gray-900">
                Products in {lga === "All Lagos" ? "Lagos" : lga}
              </h1>
              <p className="text-gray-500 text-sm mt-1 italic">
                Showing: <span className="text-orange-600 font-bold">
                  {selectedModel || selectedProduct || selectedSubCategory || "All"}
                </span>
              </p>
            </div>

            {/* Select Filters Group */}
            <div className="flex flex-wrap gap-2">
              <select
                value={lga}
                onChange={(e) => setLga(e.target.value)}
                className="bg-gray-50 border border-gray-200 px-3 py-2 rounded-xl text-xs font-bold outline-none cursor-pointer"
              >
                {lagosLGAs.map((item, idx) => (
                  <option key={idx} value={item}>{item}</option>
                ))}
              </select>

              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="bg-gray-50 border border-gray-200 px-3 py-2 rounded-xl text-xs font-bold outline-none cursor-pointer"
              >
                <option value="">Select Product</option>
                {relatedBrands.map((brand, idx) => (
                  <option key={idx} value={brand}>{brand}</option>
                ))}
              </select>

              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={models.length === 0}
                className="bg-gray-50 border border-gray-200 px-3 py-2 rounded-xl text-xs font-bold outline-none disabled:opacity-50"
              >
                <option value="">Select Model</option>
                {models.map((m, idx) => <option key={idx} value={m}>{m}</option>)}
              </select>

              <select
                value={sortFilter}
                onChange={(e) => setSortFilter(e.target.value)}
                className="bg-gray-50 border border-gray-200 px-3 py-2 rounded-xl text-xs font-bold outline-none"
              >
                <option value="All">All Items</option>
                <option value="Newly Added">Newly Added</option>
              </select>
            </div>
          </div>

          {/* Related Brand Chips */}
          {relatedBrands.length > 0 && (
            <div className="flex items-center gap-2 mt-6 overflow-x-auto pb-2 no-scrollbar">
              <span className="text-xs font-bold text-gray-400 whitespace-nowrap uppercase tracking-widest">Quick Select:</span>
              {relatedBrands.map((brand, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedProduct(brand)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all border
                    ${selectedProduct === brand
                      ? "bg-teal-600 text-white border-teal-600 shadow-lg shadow-teal-100"
                      : "bg-white text-gray-600 border-gray-200 hover:border-teal-400 hover:text-teal-600"
                    }`}
                >
                  {brand}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Loading / Error States */}
        {loading && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-64 bg-gray-200 animate-pulse rounded-[2rem]" />
            ))}
          </div>
        )}

        {/* 📦 Products Grid (Premium Card Design) */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {!loading && products.map((product) => (
            <div
              key={product._id}
              className="group bg-white rounded-[2rem] border border-gray-100 hover:border-teal-500 transition-all duration-300 flex flex-col overflow-hidden shadow-sm hover:shadow-xl"
            >
              <div className="relative h-32 sm:h-48 overflow-hidden">
                <img 
                  src={product.images?.[0] || "/placeholder.jpg"} 
                  alt={product.name} 
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                />
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-sm">
                  <FaMapMarkerAlt className="text-teal-600" />
                  {product.location?.lga || "Lagos"}
                </div>
                <div className="absolute bottom-2 left-2 bg-orange-600 text-white px-2 py-1 rounded-lg text-[10px] font-black shadow-lg">
                  ₦{product.price?.toLocaleString()}
                </div>
              </div>

              <div className="p-3 sm:p-5 flex flex-col flex-1">
                <h2 className="font-black text-gray-900 text-sm sm:text-lg truncate group-hover:text-teal-600 transition">
                  {product.name}
                </h2>
                
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-md text-[9px] font-bold uppercase">
                    {product.subCategory}
                  </span>
                  {product.model && (
                    <span className="px-2 py-0.5 bg-orange-50 text-orange-600 rounded-md text-[9px] font-bold uppercase">
                      {product.model}
                    </span>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-50">
                  <button
                    onClick={() => navigate(`/view-product/${product._id}`)}
                    className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-2.5 sm:py-3 rounded-2xl hover:bg-teal-600 transition shadow-lg shadow-gray-200 text-[11px] sm:text-sm font-bold"
                  >
                    <FaBoxOpen />
                    View Product
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State / Redirect (Premium Pattern) */}
        {!loading && products.length === 0 && (
          <div className="text-center py-20 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-gray-900">No matches found</h3>
            <p className="text-gray-500 max-w-xs mx-auto mt-2 px-4 text-sm">
              Redirecting you to <b>Sellers</b> who might have this in stock...
            </p>
            <div className="mt-6 flex justify-center">
              <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-600 animate-[loading_2s_ease-in-out_infinite]" style={{width: '50%'}}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
}